import { useState } from 'react';
import { supabase } from '../../lib/supabase/client';
import { Loader2 } from 'lucide-react';

type OAuthProvider = 'google' | 'azure' | 'apple';

interface OAuthButtonsProps {
  mode: 'login' | 'signup';
  onError: (message: string) => void;
}

interface ProviderConfig {
  provider: OAuthProvider;
  label: string;
  icon: React.ReactNode;
  bgClass: string;
  textClass: string;
  hoverClass: string;
}

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="currentColor"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="currentColor"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const MicrosoftIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#F25022" d="M1 1h10v10H1z" />
    <path fill="#00A4EF" d="M1 13h10v10H1z" />
    <path fill="#7FBA00" d="M13 1h10v10H13z" />
    <path fill="#FFB900" d="M13 13h10v10H13z" />
  </svg>
);

const AppleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const providers: ProviderConfig[] = [
  {
    provider: 'google',
    label: 'Google',
    icon: <GoogleIcon />,
    bgClass: 'bg-white/5 border border-white/10',
    textClass: 'text-text-secondary',
    hoverClass: 'hover:bg-white/10 hover:text-text-primary hover:border-white/20',
  },
  {
    provider: 'azure',
    label: 'Microsoft',
    icon: <MicrosoftIcon />,
    bgClass: 'bg-white/5 border border-white/10',
    textClass: 'text-text-secondary',
    hoverClass: 'hover:bg-white/10 hover:text-text-primary hover:border-white/20',
  },
  {
    provider: 'apple',
    label: 'Apple',
    icon: <AppleIcon />,
    bgClass: 'bg-white/5 border border-white/10',
    textClass: 'text-text-secondary',
    hoverClass: 'hover:bg-white/10 hover:text-text-primary hover:border-white/20',
  },
];

export function OAuthButtons({ mode, onError }: OAuthButtonsProps) {
  const [loadingProvider, setLoadingProvider] = useState<OAuthProvider | null>(null);

  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    setLoadingProvider(provider);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      });

      if (error) {
        onError(error.message);
      }
    } catch {
      onError(`Failed to sign in with ${provider}. Please try again.`);
    } finally {
      setLoadingProvider(null);
    }
  };

  const actionText = mode === 'login' ? 'Continue with' : 'Sign up with';

  return (
    <div className="space-y-3">
      {providers.map((config) => (
        <button
          key={config.provider}
          onClick={() => handleOAuthSignIn(config.provider)}
          disabled={loadingProvider !== null}
          className={`w-full py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed ${config.bgClass} ${config.textClass} ${config.hoverClass}`}
        >
          {loadingProvider === config.provider ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            config.icon
          )}
          {actionText} {config.label}
        </button>
      ))}
    </div>
  );
}
